import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/SimpleAuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: '',
    phone_number: '',
    id_number: '',
    date_of_birth: '',
    gender: '',
    race: '',
    city: '',
    address: '',
    postal_code: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.first_name) newErrors.first_name = 'First name is required';
    if (!formData.last_name) newErrors.last_name = 'Last name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    // Phone number validation (South African format)
    if (formData.phone_number && !/^(\+27|0)[0-9]{9}$/.test(formData.phone_number.replace(/\s/g, ''))) {
      newErrors.phone_number = 'Please enter a valid South African phone number';
    }

    // ID number validation (South African format)
    if (formData.id_number && !/^[0-9]{13}$/.test(formData.id_number)) {
      newErrors.id_number = 'Please enter a valid 13-digit South African ID number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      const result = await register(formData);
      
      if (result.success) {
        navigate('/', { replace: true });
      } else {
        setErrors({ 
          general: result.error,
          ...result.errors 
        });
      }
    } catch (error) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="auth-register-page">
      <div className="auth-register-layout">
        <header className="auth-register-intro">
          <h1>Create your account</h1>
          <p>Join GMSP3 Career Guidance to explore training and career opportunities.</p>
        </header>

        <section className="auth-register-card" aria-label="Create your account">
          <form className="auth-register-form" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="auth-register-error" role="alert">
                {errors.general}
              </div>
            )}

            <section className="auth-register-section" aria-labelledby="register-personal-heading">
              <h2 id="register-personal-heading">Personal details</h2>
              <div className="auth-register-grid">
                <div className="auth-register-field">
                  <label htmlFor="first_name">First name <span aria-hidden="true">*</span></label>
                  <input id="first_name" name="first_name" type="text" autoComplete="given-name" required
                    className={errors.first_name ? 'has-error' : ''} placeholder="Enter your first name"
                    value={formData.first_name} onChange={handleChange} />
                  {errors.first_name && <p className="auth-register-field-error">{errors.first_name}</p>}
                </div>
                <div className="auth-register-field">
                  <label htmlFor="last_name">Last name <span aria-hidden="true">*</span></label>
                  <input id="last_name" name="last_name" type="text" autoComplete="family-name" required
                    className={errors.last_name ? 'has-error' : ''} placeholder="Enter your last name"
                    value={formData.last_name} onChange={handleChange} />
                  {errors.last_name && <p className="auth-register-field-error">{errors.last_name}</p>}
                </div>
              </div>
            </section>

            <section className="auth-register-section" aria-labelledby="register-contact-heading">
              <h2 id="register-contact-heading">Contact information</h2>
              <div className="auth-register-field">
                <label htmlFor="email">Email address <span aria-hidden="true">*</span></label>
                <input id="email" name="email" type="email" autoComplete="email" required
                  className={errors.email ? 'has-error' : ''} placeholder="name@example.co.za"
                  value={formData.email} onChange={handleChange} />
                {errors.email && <p className="auth-register-field-error">{errors.email}</p>}
              </div>
              <div className="auth-register-field">
                <label htmlFor="phone_number">Phone number <span className="auth-register-optional">Optional</span></label>
                <input id="phone_number" name="phone_number" type="tel" autoComplete="tel"
                  className={errors.phone_number ? 'has-error' : ''} placeholder="e.g. 012 345 6789"
                  value={formData.phone_number} onChange={handleChange} />
                {errors.phone_number && <p className="auth-register-field-error">{errors.phone_number}</p>}
              </div>
            </section>

            <section className="auth-register-section" aria-labelledby="register-password-heading">
              <h2 id="register-password-heading">Secure your account</h2>
              <div className="auth-register-grid">
                <div className="auth-register-field">
                  <label htmlFor="password">Password <span aria-hidden="true">*</span></label>
                  <div className="auth-register-password">
                    <input id="password" name="password" type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password" required className={errors.password ? 'has-error' : ''}
                      placeholder="At least 8 characters" value={formData.password} onChange={handleChange} />
                    <button type="button" className="auth-register-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}>
                      {showPassword ? <EyeSlashIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}
                    </button>
                  </div>
                  {errors.password && <p className="auth-register-field-error">{errors.password}</p>}
                </div>
                <div className="auth-register-field">
                  <label htmlFor="confirm_password">Confirm password <span aria-hidden="true">*</span></label>
                  <div className="auth-register-password">
                    <input id="confirm_password" name="confirm_password"
                      type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required
                      className={errors.confirm_password ? 'has-error' : ''} placeholder="Re-enter your password"
                      value={formData.confirm_password} onChange={handleChange} />
                    <button type="button" className="auth-register-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}>
                      {showConfirmPassword ? <EyeSlashIcon aria-hidden="true" /> : <EyeIcon aria-hidden="true" />}
                    </button>
                  </div>
                  {errors.confirm_password && <p className="auth-register-field-error">{errors.confirm_password}</p>}
                </div>
              </div>
            </section>

            <button type="submit" disabled={isLoading} className="auth-register-submit">
              {isLoading ? (
                <span className="auth-register-spinner-wrap">
                  <span className="auth-register-spinner" aria-hidden="true" />
                  Creating account...
                </span>
              ) : 'Create account'}
            </button>
          </form>

          <footer className="auth-register-footer">
            <span>Already have an account?</span>
            <Link to="/login">Sign in</Link>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default Register;
